import React, { useState, useEffect } from "react";
import BasePrimeForm from "./BasePrimeForm";
import AmountInput from "../../../components/Utils/AmountInput";

const PrimeImposable = ({ id, prime, setPrime, closeEdit }) => {
    const [typeValeur, setTypeValeur] = useState('brut');
    const [showModal, setShowModal] = useState(false);
    const [modalSteps, setModalSteps] = useState([]);
    const [isProratise, setIsProratise] = useState(false);

    useEffect(() => {
        if (prime.category) {
            // Déterminer le type de valeur et le statut proratisé basé sur la catégorie
            if (prime.category === "BRUT") {
                setTypeValeur('brut');
                setIsProratise(false);
            } else if (prime.category === "CBP") {
                setTypeValeur('brut');
                setIsProratise(true);
            } else if (prime.category === "PANEX") {
                setTypeValeur('net');
                setIsProratise(false);
            } else if (prime.category === "PN") {
                setTypeValeur('net');
                setIsProratise(false);
            } else if (prime.category === "PNP") {
                setTypeValeur('net');
                setIsProratise(true);
            }
        }
    }, [prime.category]);

    const updatePrimeCategory = (typeVal, proratise, montantType = null) => {
        let category;
        
        if (typeVal === 'brut') {
            category = proratise ? 'CBP' : 'BRUT';
        } else { 
            if (proratise) {
                category = 'PNP';
            } else {
                category = (montantType === 'received') ? 'PANEX' : 'PN';
            }
        }
        
        setPrime(p => ({ ...p, category }));
    };

    const handleTypeValeurChange = ({ target: { value } }) => {
        setTypeValeur(value);
        updatePrimeCategory(value, isProratise);
    };

    const handleProratiseChange = ({ target: { checked } }) => {
        setIsProratise(checked);
        updatePrimeCategory(typeValeur, checked);
    };

    const handleMontantChange = (newValue) => {
        setPrime(p => ({
            ...p,
            valeur: newValue
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPrime(p => ({ ...p, [name]: value }));
    };

    const handleMontantStatutChange = ({ target: { value } }) => {
        updatePrimeCategory(typeValeur, isProratise, value);
    };

    const onBeforeSave = (primeToSave) => {
        // Si ce n'est pas une prime "nette", on utilise l'ancienne logique
        if (typeValeur !== 'net') {
            if (!primeToSave.category) {
                primeToSave.category = "BRUT";
            }
            return primeToSave; // On laisse BasePrimeForm continuer
        }

        // Pour une prime "nette", on affiche le modal et on gère le process
        return new Promise(async (resolve) => {
            const steps = [
                { text: "Activation de la règle de calcul inverse...", status: "pending" },
                { text: "Calcul du brut à partir du net fourni...", status: "pending" },
                { text: "Création/Mise à jour de la prime...", status: "pending" },
            ];
            setModalSteps(steps);
            setShowModal(true);

            const updateStep = (index, status, newText) => {
                setModalSteps(prev => prev.map((step, i) => i === index ? { ...step, status, text: newText || step.text } : step));
            };

            // Simuler les étapes asynchrones
            await new Promise(res => setTimeout(res, 1000));
            updateStep(0, "done");

            await new Promise(res => setTimeout(res, 1500));
            updateStep(1, "done", `Calcul du brut à partir du net fourni terminé.`);

            await new Promise(res => setTimeout(res, 500));
            updateStep(2, "done");

            // Indiquer que tout est terminé
            setModalSteps(prev => [...prev, { text: "Opération terminée avec succès.", status: "done" }]);

            // Fermer le modal après un délai
            setTimeout(() => {
                setShowModal(false);
                resolve(primeToSave); // On renvoie la prime modifiée à BasePrimeForm pour la sauvegarde
            }, 3500);
        });
    };

    return (
        <>
            <BasePrimeForm
                id={id}
                prime={prime}
                setPrime={setPrime}
                closeEdit={closeEdit}
                title="Prime Imposable"
                onBeforeSave={onBeforeSave}
            >
                <div className="form-control">
                    <label className="label"><span className="label-text">Nom</span></label>
                    <input type="text" className="input input-bordered w-1/2" placeholder="Nom de la prime" name="name" value={prime.name || ''} onChange={handleChange} required />
                </div>
                <div className="form-control">
                    <label className="label"><span className="label-text">Position</span></label>
                    <input type="text" className="input input-bordered w-1/2" name="position" placeholder="Position" value={prime.position || ''} onChange={handleChange} />
                </div>
                <div className="form-control">
                    <label className="label"><span className="label-text">Montant</span></label>
                    <AmountInput
                        value={prime.valeur}
                        onChange={handleMontantChange}
                        className="input input-bordered w-1/2"
                        required
                    />
                </div>
                <div className="form-control">
                    <label className="label"><span className="label-text">Type de Montant</span></label>
                    <select className="select select-bordered w-1/2" value={typeValeur} onChange={handleTypeValeurChange} disabled={!!id}>
                        <option value='brut'>Montant Brut</option>
                        <option value='net'>Montant Net</option>
                    </select>
                    <p className="mt-2 text-sm text-gray-500"><span className="text-red-500">*</span>{typeValeur === 'brut' ? " Le Montant brut est le montant avant déductions." : " Le Montant net est le montant après déductions."}</p>
                </div>
                <div className="form-control">
                    <label className="label cursor-pointer justify-start gap-2">
                        <input 
                            type="checkbox" 
                            className="checkbox" 
                            checked={isProratise}
                            onChange={handleProratiseChange}
                            disabled={!!id}
                        />
                        <span className="label-text">Proratisé</span>
                    </label>
                    <p className="mt-1 text-sm text-gray-500">Cochez si cette prime doit être proratisée selon le nombre de jours travaillés.</p>
                </div>
                {(typeValeur === 'net' && !isProratise) && (
                    <div className="form-control">
                        <label className="label"><span className="label-text">Statut du Montant</span></label>
                        <select
                            className="select select-bordered w-1/2"
                            value={prime.category === "PANEX" ? "received" : "added_to_salary"}
                            onChange={handleMontantStatutChange}
                            disabled={!!id}
                        >
                            <option value='received'>Déjà reçu</option>
                            <option value='added_to_salary'>Ajouté au salaire</option>
                        </select>
                    </div>
                )}
            </BasePrimeForm>

            {showModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">Traitement de la prime spéciale</h3>
                        <div className="mt-4 space-y-4">
                            {modalSteps.map((step, index) => (
                                <div key={index} className="flex items-center gap-4">
                                    <div className="w-6 h-6">
                                        {step.status === 'pending' && <span className="loading loading-spinner"></span>}
                                        {step.status === 'done' && (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6 text-success" fill="none" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        )}
                                    </div>
                                    <span>{step.text}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default PrimeImposable;